import { useState, useMemo } from 'react';
import { Upload, X, AlignLeft, AlignCenter, AlignRight, FileText, Maximize2 } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Template, templateVariables, TemplateVariable } from '@/types/template';

interface PDFDesignInterfaceProps {
  pdfSettings: Template['pdfSettings'];
  onSettingsChange: (settings: Template['pdfSettings']) => void;
  onInsertVariable?: (variableKey: string) => void;
}

const DEFAULT_PDF_SETTINGS: NonNullable<Template['pdfSettings']> = {
  pageLayout: {
    size: 'A4',
    orientation: 'portrait',
    margins: {
      top: 20,
      right: 20,
      bottom: 20,
      left: 20,
    },
  },
  header: {
    enabled: false,
    content: '',
    height: 50,
  },
  footer: {
    enabled: true,
    content: 'Page {{page}} of {{totalPages}}',
    height: 40,
  },
  styling: {
    fontFamily: 'Arial',
    fontSize: 12,
    primaryColor: '#000000',
    secondaryColor: '#666666',
    lineSpacing: 1.5,
  },
};

export function PDFDesignInterface({ pdfSettings, onSettingsChange, onInsertVariable }: PDFDesignInterfaceProps) {
  const settings = pdfSettings || DEFAULT_PDF_SETTINGS;
  const [logoPreview, setLogoPreview] = useState<string | null>(settings.logo?.url || null);

  // Group variables by category
  const groupedVariables = useMemo(() => {
    return templateVariables.reduce((acc, variable) => {
      if (!acc[variable.category]) {
        acc[variable.category] = [];
      }
      acc[variable.category].push(variable);
      return acc;
    }, {} as Record<string, TemplateVariable[]>);
  }, []);

  const updateSettings = (updates: Partial<NonNullable<Template['pdfSettings']>>) => {
    onSettingsChange({ ...settings, ...updates });
  };

  const handleInsertVariable = (variableKey: string) => {
    if (onInsertVariable) {
      onInsertVariable(variableKey);
    }
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload a valid image file (PNG, JPG, or SVG)');
        return;
      }

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        const url = e.target?.result as string;
        setLogoPreview(url);
        updateSettings({
          logo: {
            url,
            position: settings.logo?.position || 'top-center',
            width: settings.logo?.width || 150,
            height: settings.logo?.height || 50,
          },
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoPreview(null);
    updateSettings({ logo: undefined });
  };

  const updateLogo = (updates: Partial<NonNullable<Template['pdfSettings']>['logo']>) => {
    if (settings.logo) {
      updateSettings({
        logo: { ...settings.logo, ...updates },
      });
    }
  };

  const updatePageLayout = (updates: Partial<NonNullable<Template['pdfSettings']>['pageLayout']>) => {
    updateSettings({
      pageLayout: { ...settings.pageLayout, ...updates },
    });
  };

  const updateMargins = (updates: Partial<NonNullable<Template['pdfSettings']>['pageLayout']['margins']>) => {
    updateSettings({
      pageLayout: {
        ...settings.pageLayout,
        margins: { ...settings.pageLayout.margins, ...updates },
      },
    });
  };

  const updateHeader = (updates: Partial<NonNullable<Template['pdfSettings']>['header']>) => {
    updateSettings({
      header: { ...settings.header!, ...updates },
    });
  };

  const updateFooter = (updates: Partial<NonNullable<Template['pdfSettings']>['footer']>) => {
    updateSettings({
      footer: { ...settings.footer!, ...updates },
    });
  };

  const updateStyling = (updates: Partial<NonNullable<Template['pdfSettings']>['styling']>) => {
    updateSettings({
      styling: { ...settings.styling, ...updates },
    });
  };

  return (
    <ScrollArea className="h-full">
      <ScrollAreaViewport className="h-full">
        <div className="space-y-6 pr-4">
          {/* Logo Upload Section */}
          <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Logo Configuration
            </CardTitle>
            <CardDescription className="text-xs">
              Upload and position your company logo
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!logoPreview ? (
              <div className="border-2 border-dashed rounded-lg p-6 text-center hover:border-primary/50 transition-colors">
                <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground mb-2">
                  Upload your logo
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  PNG, JPG, or SVG (max 2MB)
                </p>
                <Input
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => document.getElementById('logo-upload')?.click()}
                >
                  Choose File
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="relative border rounded-lg p-4 bg-muted/30">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 h-6 w-6"
                    onClick={handleRemoveLogo}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                  <div className="flex items-center justify-center h-24">
                    <img
                      src={logoPreview}
                      alt="Logo preview"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Logo Position</Label>
                    <Select
                      value={settings.logo?.position}
                      onValueChange={(value: any) => updateLogo({ position: value })}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="top-left">Top Left</SelectItem>
                        <SelectItem value="top-center">Top Center</SelectItem>
                        <SelectItem value="top-right">Top Right</SelectItem>
                        <SelectItem value="bottom-left">Bottom Left</SelectItem>
                        <SelectItem value="bottom-center">Bottom Center</SelectItem>
                        <SelectItem value="bottom-right">Bottom Right</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs">Width (px)</Label>
                      <Input
                        type="number"
                        value={settings.logo?.width}
                        onChange={(e) => updateLogo({ width: parseInt(e.target.value) || 150 })}
                        className="h-8 text-xs"
                        min="50"
                        max="500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Height (px)</Label>
                      <Input
                        type="number"
                        value={settings.logo?.height}
                        onChange={(e) => updateLogo({ height: parseInt(e.target.value) || 50 })}
                        className="h-8 text-xs"
                        min="20"
                        max="200"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Page Layout Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <Maximize2 className="h-4 w-4" />
              Page Layout
            </CardTitle>
            <CardDescription className="text-xs">
              Configure page size, orientation, and margins
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">Page Size</Label>
                <Select
                  value={settings.pageLayout.size}
                  onValueChange={(value: any) => updatePageLayout({ size: value })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A4">A4 (210 × 297 mm)</SelectItem>
                    <SelectItem value="Letter">Letter (8.5 × 11 in)</SelectItem>
                    <SelectItem value="Legal">Legal (8.5 × 14 in)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Orientation</Label>
                <Select
                  value={settings.pageLayout.orientation}
                  onValueChange={(value: any) => updatePageLayout({ orientation: value })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portrait">Portrait</SelectItem>
                    <SelectItem value="landscape">Landscape</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            <div className="space-y-3">
              <Label className="text-xs font-semibold">Margins (mm)</Label>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Top</Label>
                  <Input
                    type="number"
                    value={settings.pageLayout.margins.top}
                    onChange={(e) => updateMargins({ top: parseInt(e.target.value) || 20 })}
                    className="h-8 text-xs"
                    min="0"
                    max="50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Right</Label>
                  <Input
                    type="number"
                    value={settings.pageLayout.margins.right}
                    onChange={(e) => updateMargins({ right: parseInt(e.target.value) || 20 })}
                    className="h-8 text-xs"
                    min="0"
                    max="50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Bottom</Label>
                  <Input
                    type="number"
                    value={settings.pageLayout.margins.bottom}
                    onChange={(e) => updateMargins({ bottom: parseInt(e.target.value) || 20 })}
                    className="h-8 text-xs"
                    min="0"
                    max="50"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Left</Label>
                  <Input
                    type="number"
                    value={settings.pageLayout.margins.left}
                    onChange={(e) => updateMargins({ left: parseInt(e.target.value) || 20 })}
                    className="h-8 text-xs"
                    min="0"
                    max="50"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Header & Footer Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Header & Footer</CardTitle>
            <CardDescription className="text-xs">
              Add headers and footers to your PDF documents
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Header */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold">Header</Label>
                <Switch
                  checked={settings.header?.enabled}
                  onCheckedChange={(checked) => updateHeader({ enabled: checked })}
                />
              </div>
              {settings.header?.enabled && (
                <div className="space-y-3 pl-4 border-l-2">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Content</Label>
                    <Input
                      value={settings.header.content}
                      onChange={(e) => updateHeader({ content: e.target.value })}
                      placeholder="Enter header text..."
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Height (px)</Label>
                    <Input
                      type="number"
                      value={settings.header.height}
                      onChange={(e) => updateHeader({ height: parseInt(e.target.value) || 50 })}
                      className="h-8 text-xs"
                      min="20"
                      max="100"
                    />
                  </div>
                </div>
              )}
            </div>

            <Separator />

            {/* Footer */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold">Footer</Label>
                <Switch
                  checked={settings.footer?.enabled}
                  onCheckedChange={(checked) => updateFooter({ enabled: checked })}
                />
              </div>
              {settings.footer?.enabled && (
                <div className="space-y-3 pl-4 border-l-2">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Content</Label>
                    <Input
                      value={settings.footer.content}
                      onChange={(e) => updateFooter({ content: e.target.value })}
                      placeholder="Enter footer text..."
                      className="h-8 text-xs"
                    />
                    <p className="text-xs text-muted-foreground">
                      Use <Badge variant="outline" className="text-xs px-1">{'{{page}}'}</Badge> and{' '}
                      <Badge variant="outline" className="text-xs px-1">{'{{totalPages}}'}</Badge> for page numbers
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Height (px)</Label>
                    <Input
                      type="number"
                      value={settings.footer.height}
                      onChange={(e) => updateFooter({ height: parseInt(e.target.value) || 40 })}
                      className="h-8 text-xs"
                      min="20"
                      max="100"
                    />
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Styling Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Document Styling</CardTitle>
            <CardDescription className="text-xs">
              Customize fonts, colors, and spacing
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs">Font Family</Label>
              <Select
                value={settings.styling.fontFamily}
                onValueChange={(value) => updateStyling({ fontFamily: value })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Arial">Arial</SelectItem>
                  <SelectItem value="Helvetica">Helvetica</SelectItem>
                  <SelectItem value="Times New Roman">Times New Roman</SelectItem>
                  <SelectItem value="Courier">Courier</SelectItem>
                  <SelectItem value="Georgia">Georgia</SelectItem>
                  <SelectItem value="Verdana">Verdana</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Font Size: {settings.styling.fontSize}pt</Label>
              <Slider
                value={[settings.styling.fontSize]}
                onValueChange={([value]) => updateStyling({ fontSize: value })}
                min={8}
                max={24}
                step={1}
                className="py-2"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs">Primary Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={settings.styling.primaryColor}
                    onChange={(e) => updateStyling({ primaryColor: e.target.value })}
                    className="h-8 w-12 p-1 cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={settings.styling.primaryColor}
                    onChange={(e) => updateStyling({ primaryColor: e.target.value })}
                    className="h-8 text-xs flex-1"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Secondary Color</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={settings.styling.secondaryColor}
                    onChange={(e) => updateStyling({ secondaryColor: e.target.value })}
                    className="h-8 w-12 p-1 cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={settings.styling.secondaryColor}
                    onChange={(e) => updateStyling({ secondaryColor: e.target.value })}
                    className="h-8 text-xs flex-1"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs">Line Spacing: {settings.styling.lineSpacing.toFixed(1)}</Label>
              <Slider
                value={[settings.styling.lineSpacing]}
                onValueChange={([value]) => updateStyling({ lineSpacing: value })}
                min={1}
                max={3}
                step={0.1}
                className="py-2"
              />
            </div>
          </CardContent>
        </Card>

        {/* Available Variables Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Available Variables</CardTitle>
            <CardDescription className="text-xs">
              Select a variable to insert into PDF template
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Select onValueChange={(value) => handleInsertVariable(value)}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select a variable to insert..." />
              </SelectTrigger>
              <SelectContent className="max-h-[400px]">
                {Object.entries(groupedVariables).map(([category, variables]) => (
                  <div key={category}>
                    <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase bg-muted/50">
                      {category}
                    </div>
                    {variables.map((variable) => (
                      <SelectItem 
                        key={variable.key} 
                        value={variable.key}
                        className="pl-4"
                      >
                        <div className="flex flex-col py-1">
                          <div className="font-mono text-xs text-blue-600">
                            {variable.key}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {variable.label}
                          </div>
                          <div className="text-xs text-muted-foreground/70 italic">
                            e.g., {variable.example}
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </div>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Variables will be inserted at the cursor position in the PDF template content.
            </p>
          </CardContent>
        </Card>
        </div>
      </ScrollAreaViewport>
      <ScrollAreaScrollbar orientation="vertical" />
    </ScrollArea>
  );
}
